import { ChangeEvent, FC, FormEvent, useState } from 'react';
import { TextField, Button } from '../../../../ui';
import { IconButton, InputAdornment, Link, Stack, Typography } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

interface IProps {
    email: string;
    emailError: boolean;
    handleEmailChange: (event: ChangeEvent) => void;
    password: string;
    passwordError: boolean;
    handlePasswordChange: (event: ChangeEvent) => void;
    handleSignIn: (event: FormEvent) => void;
    handleForgotPassword: () => void;
    isBuyer?: boolean;
    /** Card-style New login: full-width fields, password toggle, CTA order. */
    useCardLayout?: boolean;
}

const fieldSx = {
    pb: 1,
    '.MuiInputLabel-shrink': {
        top: -8,
    },
};

const cardFieldSx = {
    ...fieldSx,
    width: '100%',
};

const LoginForm: FC<IProps> = ({
    email,
    emailError,
    handleEmailChange,
    password,
    passwordError,
    handlePasswordChange,
    handleSignIn,
    handleForgotPassword,
    isBuyer = false,
    useCardLayout = false,
}) => {
    const [showPassword, setShowPassword] = useState(false);

    if (useCardLayout) {
        return (
            <form onSubmit={handleSignIn} noValidate>
                <Stack spacing={2}>
                    <TextField
                        stacked
                        id="transcepta-login-email"
                        testId="login-page-email-input"
                        label="Email"
                        type="email"
                        value={email}
                        error={emailError}
                        onChange={handleEmailChange}
                        sx={cardFieldSx}
                    />
                    <TextField
                        stacked
                        id="transcepta-login-password"
                        testId="login-page-password-input"
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        error={passwordError}
                        onChange={handlePasswordChange}
                        sx={cardFieldSx}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword((v) => !v)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Button
                        type="button"
                        variant="text"
                        fullWidth
                        onClick={handleForgotPassword}
                        testid="login-page-forgot-password-button"
                        sx={{
                            textTransform: 'none',
                            justifyContent: 'center',
                            py: 1,
                        }}
                    >
                        I forgot my password
                    </Button>
                    <Button type="submit" testid="login-page-sign-in-button" variant="default" fullWidth>
                        Sign In
                    </Button>
                    {isBuyer && (
                        <Typography component="p" variant="body2" color="text.secondary" align="center">
                            Are you a Supplier/Vendor? Log in{' '}
                            <Link href="https://supplier1.transcepta.com/SupplierPortal/login">here</Link>.
                        </Typography>
                    )}
                </Stack>
            </form>
        );
    }

    return (
        <form onSubmit={handleSignIn} noValidate>
            <TextField
                stacked
                id="transcepta-login-email"
                testId="login-page-email-input"
                label="Email"
                type="email"
                color="secondary"
                value={email}
                error={emailError}
                onChange={handleEmailChange}
                sx={fieldSx}
            />
            <TextField
                stacked
                id="transcepta-login-password"
                testId="login-page-password-input"
                label="Password"
                type="password"
                color="secondary"
                value={password}
                error={passwordError}
                onChange={handlePasswordChange}
                sx={fieldSx}
            />
            <Stack direction="row" justifyContent="space-between">
                <Button variant="secondary" onClick={handleForgotPassword} testid="login-page-forgot-password-button">
                    I forgot my password
                </Button>
                <Button type="submit" testid="login-page-sign-in-button">
                    Sign In
                </Button>
            </Stack>
            {isBuyer && (
                <Typography component="p" align="right" fontSize={12}>
                    Are you a Supplier/Vendor? Log in{' '}
                    <Link href="https://supplier1.transcepta.com/SupplierPortal/login">here</Link>.
                </Typography>
            )}
        </form>
    );
};

export default LoginForm;
